// Read coalescing for the NomOS seam (round trips, behaviour-neutral). ONE
// implementation for every client: Poiesis's reads (api/reduce.ts — a record
// page's widgets, a dashboard's tiles) and Sentinel's NomOS seam
// (sentinel-frontend/src/services/nomos/batch.ts re-exports this module).
//
//
// Reads issued close together (the same tick, or within `windowMs` of the first one) travel as ONE
// POST /v1/tenants/{t}/batch. NomOS runs every item through its own route in process, with this caller's bearer,
// tenant and row scope, and streams each answer back (NDJSON) as soon as it is ready; every caller here still
// gets exactly the bytes and status that route would have answered on its own. A lone read goes out on its own.
//
// In Sentinel it sits UNDER ./cache: in-flight de-duplication and the settle/session caches run first, so only distinct reads
// that really need the network are batched. Writes never pass through here.
//
// If the batch call itself fails (an older NomOS without /batch, a network error, the tenant not yet resolved),
// every item is sent on its own instead, so batching can never turn a working read into a failing one.
/** One read's answer, as the route itself would have given it. */
export interface RawResponse { ok: boolean; status: number; text: string; url: string }

export interface BatchableRead {
  /** NomOS root ('' = same origin) and tenant: reads batch only with reads of the same root, tenant and bearer */
  baseUrl: string;
  tenant: string;
  token?: string;
  method: string;
  /** absolute path with query string, as the item's own request would use it (`/v1/tenants/t/...`, `/me`) */
  path: string;
  /** the JSON payload exactly as the single request would send it */
  payload?: string;
  /** the item's own request, used when it travels alone or the batch fails */
  send: () => Promise<RawResponse>;
  /** 'high': the read the person is waiting on (the chart they just re-dated). It goes to the FRONT of its batch:
   *  NomOS starts a batch's items in order through a few slots (monad/api/v1/batch.py MAX_CONCURRENCY), so a read
   *  queued behind thirty others waits for them. Order only: every item is still sent and answered as before. */
  priority?: ReadPriority;
}

export type ReadPriority = 'high' | 'normal';

export interface BatchOptions { enabled: boolean; windowMs: number; maxItems: number }

/** NomOS caps a batch at 50 items (monad/api/v1/batch.py MAX_ITEMS). */
export const MAX_BATCH_ITEMS = 50;
const DEFAULTS: BatchOptions = { enabled: true, windowMs: 10, maxItems: MAX_BATCH_ITEMS };
let options: BatchOptions = { ...DEFAULTS };

/** Tests/harness: tune or switch off coalescing. `null` restores the defaults. */
export function configureBatching(o: Partial<BatchOptions> | null): void {
  options = o === null ? { ...DEFAULTS } : { ...options, ...o };
}
export function batchingOptions(): BatchOptions { return { ...options }; }

type Pending = BatchableRead & { resolve: (r: RawResponse) => void; reject: (e: unknown) => void };
const queues = new Map<string, Pending[]>();
let timer: ReturnType<typeof setTimeout> | null = null;
/** set once NomOS answered /batch with 404/405: this server has no batch route, stop trying */
let unsupported = false;

/** Diagnostics/tests: batch calls sent and items they carried. */
export const batchStats = { batches: 0, items: 0, single: 0 };
export function resetBatchState(): void {
  // reads already queued were built for the old config: send them as they would have gone without batching
  const queued = [...queues.values()].flat();
  queues.clear(); unsupported = false;
  if (timer) { clearTimeout(timer); timer = null; }
  queued.forEach(alone);
  batchStats.batches = 0; batchStats.items = 0; batchStats.single = 0;
}

/** POST routes NomOS runs inside a batch (mirror of monad/api/v1/batch.py _POST_READS). */
const POST_READS = [/^objects\/[^/]+\/records\/(query|values)$/, /^queries$/, /^catalog\/search$/, /^readings\/count$/,
  /^readings\/latest-actual$/, /^records\/chips$/, /^meter-bindings\/query$/, /^connection-loads\/query$/];

/** Whether NomOS accepts this read inside a batch for `tenant` (anything else would refuse the whole batch). */
export function batchable(method: string, path: string, tenant: string): boolean {
  const m = method.toUpperCase();
  const p = path.split('?')[0];
  const t = `/tenants/${encodeURIComponent(tenant)}`;
  if (p.startsWith(`/v1${t}/`)) {
    const rest = p.slice(`/v1${t}/`.length);
    if (rest === 'batch' || rest.startsWith('batch/')) return false;
    return m === 'GET' || (m === 'POST' && POST_READS.some((r) => r.test(rest)));
  }
  return m === 'GET' && (p === '/me' || p === t || p.startsWith(`${t}/`));
}

/** Queue a read for the next batch (or send it now when batching is off or NomOS would not batch it). */
export function batchedRead(read: BatchableRead): Promise<RawResponse> {
  if (!options.enabled || unsupported || !read.tenant || !batchable(read.method, read.path, read.tenant)) return read.send();
  return new Promise<RawResponse>((resolve, reject) => {
    const key = `${read.baseUrl}|${read.tenant}|${read.token ?? ''}`;
    const q = queues.get(key) ?? [];
    q.push({ ...read, resolve, reject });
    queues.set(key, q);
    if (!timer) timer = setTimeout(flush, options.windowMs);
  });
}

/** High-priority reads first, otherwise in the order they were issued (a stable partition). */
function ordered(q: Pending[]): Pending[] {
  return q.some((p) => p.priority === 'high') ? [...q.filter((p) => p.priority === 'high'), ...q.filter((p) => p.priority !== 'high')] : q;
}

/**
 * Raise a read that is still waiting for the next batch to 'high'. A read the person waits on can share an identical
 * read another card issued first (./cache de-duplicates it); that queued read then carries the priority.
 * Returns whether a queued read matched.
 */
export function prioritise(read: Pick<BatchableRead, 'baseUrl' | 'tenant' | 'token' | 'method' | 'path' | 'payload'>): boolean {
  const q = queues.get(`${read.baseUrl}|${read.tenant}|${read.token ?? ''}`);
  const hit = q?.find((p) => p.method === read.method && p.path === read.path && p.payload === read.payload);
  if (hit) hit.priority = 'high';
  return !!hit;
}

function flush(): void {
  timer = null;
  const groups = [...queues.values()].map(ordered);
  queues.clear();
  for (const q of groups) {
    for (let i = 0; i < q.length; i += options.maxItems) void sendGroup(q.slice(i, i + options.maxItems));
  }
}

function alone(p: Pending): void {
  batchStats.single++;
  p.send().then(p.resolve, p.reject);
}

async function sendGroup(items: Pending[]): Promise<void> {
  if (items.length === 1) { alone(items[0]); return; }
  const { baseUrl, tenant, token } = items[0];
  const headers: Record<string, string> = { Accept: 'application/x-ndjson', 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const url = `${baseUrl}/v1/tenants/${encodeURIComponent(tenant)}/batch`;
  // payloads are already JSON text: splice them in rather than parse + re-serialise
  const body = `{"requests":[${items.map((it) => `{"method":${JSON.stringify(it.method.toUpperCase())},"path":${JSON.stringify(it.path)}${it.payload !== undefined ? `,"body":${it.payload}` : ''}}`).join(',')}]}`;
  const settled = new Set<number>();
  batchStats.batches++; batchStats.items += items.length;
  try {
    const res = await fetch(url, { method: 'POST', headers, body });
    if (!res.ok) {
      if (res.status === 404 || res.status === 405) unsupported = true;
      throw new Error(`batch ${res.status}`);
    }
    const onLine = (line: string) => {
      if (!line.trim()) return;
      const r = parseResult(line);
      const it = items[r.index];
      if (!it || settled.has(r.index)) return;
      settled.add(r.index);
      it.resolve({ ok: r.status >= 200 && r.status < 300, status: r.status, text: r.text, url: `${baseUrl}${it.path}` });
    };
    const ctype = res.headers.get('content-type') ?? '';
    if (ctype.includes('ndjson') && res.body && typeof res.body.getReader === 'function') {
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let nl: number;
        while ((nl = buf.indexOf('\n')) >= 0) { onLine(buf.slice(0, nl)); buf = buf.slice(nl + 1); }
      }
      onLine(buf + dec.decode());
    } else {
      const text = await res.text();
      if (ctype.includes('ndjson')) text.split('\n').forEach(onLine);
      else ((JSON.parse(text) as { data?: unknown[] }).data ?? []).forEach((r) => onLine(JSON.stringify(r)));
    }
  } catch {
    /* fall through: every item not answered is sent on its own */
  }
  items.forEach((it, i) => { if (!settled.has(i)) alone(it); });
}

const HEAD = /^\{"index":(\d+),"status":(\d+),"body":/;

/** One result line -> index, status and the item's response text (a JSON body is sliced out verbatim). */
export function parseResult(line: string): { index: number; status: number; text: string } {
  const m = HEAD.exec(line);
  if (m) return { index: Number(m[1]), status: Number(m[2]), text: line.slice(m[0].length, line.lastIndexOf('}')) };
  const o = JSON.parse(line) as { index: number; status: number; body?: unknown; text?: string };
  return { index: o.index, status: o.status, text: o.body !== undefined ? JSON.stringify(o.body) : o.text ?? '' };
}
